import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Tooltip
} from '@mui/material'
import React, { useState } from 'react'
import CloseButton from '../../common/components/CloseButton'
import useMutation from '../../hooks/useMutation'
import { useAddAiModuleMutation } from 'src/api/trainingTopics'
import { Info } from '@mui/icons-material'

const promptDescriptionMore = {
  title: 'Short Input',
  text: 'Provide a description or idea for the training module you want to generate. The AI will use your input to create a custom module'
}
const promptDescriptionEnough = {
  title: 'Sufficient Input',
  text: `Great! This description provides enough detail to generate a solid training module. If you'd like to refine it further (e.g., methodology, duration, difficulty level), feel free to add more specifics.`
}

export default function GenerateModuleModal({ handleClose, open, topicId, onError = () => {} }) {
  const [topicName, setTopicName] = useState('')
  const [prompt, setPrompt] = useState('')
  const isDisabled = !topicName.trim() || !prompt.trim()

  const { submit: submitCreate, isLoading } = useMutation(useAddAiModuleMutation, {
    successMessage: 'Module successfully generated',
    onSuccess: () => {
      handleClose()
    },
    onError: () => {
      onError()
    }
  })

  function handleSubmit() {
    const payload = { topicId, name: topicName, prompt }
    submitCreate(payload)
  }

  const handleCancel = () => {
    handleClose()
    setTopicName('')
  }

  return (
    <Dialog open={open} onClose={isLoading ? () => {} : handleCancel} sx={styles.dialog}>
      <DialogTitle sx={styles.title}>
        <Typography sx={styles.titleText}>{'Generate module'}</Typography>
        <Tooltip title={isLoading ? 'Cannot close while training is generating' : null} arrow>
          <div style={styles.closeButton}>
            <CloseButton disabled={isLoading} onClick={handleCancel} />
          </div>
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <TextField
          disabled={isLoading}
          autoFocus
          label='Module Name'
          fullWidth
          value={topicName}
          onChange={e => setTopicName(e.target.value)}
          sx={styles.textField}
        />
        <TextField
          disabled={isLoading}
          minRows={4}
          label='Module Description'
          fullWidth
          multiline
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          sx={styles.textField}
        />

        <div style={{ background: '#E0E9FF', border: '1px solid #cddbff', borderRadius: 5, padding: '20px 25px' }}>
          <div className='row row-center' style={{ marginBottom: 10, color: '#3870ff' }}>
            <Info sx={{ marginRight: 1, opacity: '0.9' }} />
            <p style={{ fontSize: 18, fontWeight: 500 }}>
              {prompt.length < 100 ? promptDescriptionMore.title : promptDescriptionEnough.title}
            </p>
          </div>
          <p>{prompt.length < 100 ? promptDescriptionMore.text : promptDescriptionEnough.text}</p>
        </div>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button
          onClick={handleSubmit}
          startIcon={
            isLoading && (
              <CircularProgress
                sx={{
                  '& svg': {
                    color: '#ffffff'
                  }
                }}
                size={'20px'}
              />
            )
          }
          disabled={isDisabled || isLoading}
          variant='contained'
          fullWidth
          sx={styles.button}
        >
          {isLoading ? 'Generating...' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
const styles = {
  dialog: {
    '& .MuiPaper-root': {
      borderRadius: '16px',
      maxWidth: 'unset',
      width: '570px'
    }
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: '8px'
  },
  titleText: {
    color: '#1B1B26',
    fontFamily: 'Satoshi',
    fontWeight: 900,
    fontSize: 35,
    textAlign: 'center'
  },
  closeButton: {
    position: 'absolute',
    right: '16px',
    top: '16px'
  },
  content: {
    p: 3,
    width: '570px'
  },
  textField: {
    mt: 1,
    mb: 2,
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px'
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Plus Jakarta Sans',
      fontWeight: 400,
      fontSize: '16px'
    }
  },
  actions: {
    p: 3,
    justifyContent: 'center'
  },
  button: {
    height: '60px',
    borderRadius: '14px',
    backgroundColor: '#6123FF',
    color: '#fff',
    textTransform: 'none',
    fontFamily: 'Plus Jakarta Sans',
    width: '100%',
    '&:hover': {
      backgroundColor: '#7843ff'
    }
  }
}
